(() => {
  if (window.__impulseFreeShippingInitialized) return;
  window.__impulseFreeShippingInitialized = true;

  const CONFIG_SELECTOR = '[data-impulse-free-shipping-config]';
  const PROGRESS_SELECTOR = '[data-impulse-free-shipping-progress]';
  const SUMMARY_SELECTORS = ['.cart-drawer__summary', '.cart-summary__inner'];

  let cartState = null;
  let fetchSequence = 0;
  let mountFrame = null;

  const getConfig = () => {
    const element = document.querySelector(CONFIG_SELECTOR);
    if (!element) return null;

    const thresholdCents = Number.parseInt(element.dataset.thresholdCents || '', 10);
    if (!Number.isFinite(thresholdCents) || thresholdCents <= 0) return null;

    return {
      thresholdCents,
      currency: element.dataset.currency || 'EUR',
    };
  };

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency,
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (_error) {
      return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
    }
  };

  const createProgress = () => {
    const element = document.createElement('div');
    element.className = 'impulse-free-shipping-progress';
    element.setAttribute('data-impulse-free-shipping-progress', '');
    element.innerHTML = `
      <p class="impulse-free-shipping-progress__message" aria-live="polite"></p>
      <div
        class="impulse-free-shipping-progress__track"
        role="progressbar"
        aria-label="Progression vers la livraison offerte"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow="0"
      >
        <span class="impulse-free-shipping-progress__fill"></span>
      </div>
    `;
    return element;
  };

  const getDirectProgress = (container) =>
    Array.from(container.children).find((child) => child.matches?.(PROGRESS_SELECTOR)) || null;

  const updateProgress = (element, cart, config) => {
    const totalCents = Math.max(0, Number(cart.total_price) || 0);
    const remainingCents = Math.max(0, config.thresholdCents - totalCents);
    const reached = remainingCents === 0;
    const percentage = Math.min(100, Math.max(0, (totalCents / config.thresholdCents) * 100));
    const currency = cart.currency || config.currency;

    const message = reached
      ? 'Livraison offerte !'
      : `Plus que ${formatMoney(remainingCents, currency)} pour la livraison offerte`;

    const messageElement = element.querySelector('.impulse-free-shipping-progress__message');
    const trackElement = element.querySelector('.impulse-free-shipping-progress__track');

    if (messageElement && messageElement.textContent !== message) {
      messageElement.textContent = message;
    }

    element.dataset.reached = String(reached);
    element.style.setProperty('--impulse-free-shipping-progress', `${percentage}%`);

    if (trackElement) {
      trackElement.setAttribute('aria-valuenow', String(Math.round(percentage)));
      trackElement.setAttribute('aria-valuetext', message);
    }
  };

  const removeProgressBars = () => {
    document.querySelectorAll(PROGRESS_SELECTOR).forEach((element) => element.remove());
  };

  const mountProgressBars = () => {
    mountFrame = null;
    const config = getConfig();

    if (!config) {
      removeProgressBars();
      return;
    }

    if (!cartState) return;

    SUMMARY_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((container) => {
        let progress = getDirectProgress(container);

        if (!progress) {
          progress = createProgress();
          container.prepend(progress);
        }

        updateProgress(progress, cartState, config);
      });
    });
  };

  const scheduleMount = () => {
    if (mountFrame !== null) return;
    mountFrame = window.requestAnimationFrame(mountProgressBars);
  };

  const fetchCart = async () => {
    const sequence = ++fetchSequence;
    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return;

      const cart = await response.json();
      if (sequence !== fetchSequence) return;

      cartState = cart;
      scheduleMount();
    } catch (_error) {
      // The cart remains fully usable if the informational progress bar cannot refresh.
    }
  };

  const init = () => {
    fetchCart();

    document.addEventListener('cart:update', fetchCart);
    document.addEventListener('discount:update', fetchCart);
    document.addEventListener('shopify:section:load', fetchCart);
    window.addEventListener('pageshow', fetchCart);

    const observer = new MutationObserver(scheduleMount);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
